const NODE_POSITION_OFFSET_Y = 400;
const BLOCK_TYPE_TO_NODE_TITLE: Record<string, string> = {
  "form-block": "User fills out form",
  "table-listing-block": "View on table",
  "kanban-block": "Manage on board",
  "chatbot-block": "User chats with bot",
};

interface ZSL {
  name?: string;
  description?: string;
  zaps?: Array<{
    id: string;
    title?: string;
    zdl?: {
      steps?: Array<{
        params?: Record<string, any>;
        steps?: Array<{
          steps?: Array<{
            params?: Record<string, any>;
          }>;
        }>;
      }>;
    };
  }>;
  tables?: Array<{
    id: string;
    name?: string;
    fields?: Array<{
      options?: {
        choice_provider?: {
          with_table_id?: string;
        };
      };
    }>;
  }>;
  chatbots?: Array<{
    id: string;
    config?: {
      chatbotName?: string;
      triggers?: Array<{
        config?: {
          collectUserDataTableId?: string;
        };
      }>;
    };
  }>;
  interfaces?: Array<{
    id: string;
    pages?: Array<{
      id: string;
      title?: string;
      blocks?: Array<{
        type?: string;
        config?: Record<string, any>;
        children?: Array<{
          config?: Record<string, any>;
        }>;
      }>;
    }>;
  }>;
}

interface CanvasData {
  canvas: {
    name: string;
    description: string;
    nodes: Array<{
      id: string;
      type: string;
      position: { x: number; y: number };
      data: Record<string, any>;
      style?: Record<string, any>;
    }>;
    edges: Array<{
      id: string;
      source: string;
      target: string;
      markerEnd?: {
        type: string;
        strokeWidth: number;
        color: string;
      };
    }>;
  };
  warnings: string[];
}

export function parseCanvasDataFromZSL(zsl: ZSL): CanvasData {
  const data: CanvasData = {
    canvas: {
      name: zsl.name || "",
      description: zsl.description || "",
      nodes: [],
      edges: [],
    },
    warnings: [],
  };

  const { nodes, edges } = data.canvas;
  const { warnings } = data;
  const assetIdsToNodeIds: Record<string, string> = {};

  addZapNodes(zsl, nodes, assetIdsToNodeIds);
  addTableNodes(zsl, nodes, assetIdsToNodeIds);
  addChatbotNodes(zsl, nodes, assetIdsToNodeIds);
  addInterfaceNodes(zsl, nodes, assetIdsToNodeIds);

  addZapEdges(zsl, edges, assetIdsToNodeIds, warnings);
  addTableEdges(zsl, edges, assetIdsToNodeIds, warnings);
  addChatbotEdges(zsl, edges, assetIdsToNodeIds, warnings);
  addInterfaceEdges(zsl, edges, assetIdsToNodeIds, warnings);

  addTitleTextNode(zsl, nodes);

  return data;
}

function addTitleTextNode(
  zsl: ZSL,
  nodes: CanvasData["canvas"]["nodes"],
): void {
  const titleText = zsl.name;
  const subtitleText = zsl.description;
  if (!titleText && !subtitleText) return;

  const content: Array<{
    type: string;
    content?: Array<{ type: string; text: string }>;
    attrs?: { level: number };
  }> = [];

  if (titleText) {
    content.push({
      type: "heading",
      content: [{ type: "text", text: titleText }],
    });
  }
  if (subtitleText) {
    content.push({
      type: "heading",
      attrs: { level: 2 },
      content: [{ type: "text", text: subtitleText }],
    });
  }

  nodes.push({
    id: `draft-text-${titleText || "untitled"}`,
    type: "text",
    position: { x: 0, y: -100 },
    style: { backgroundColor: "transparent" },
    data: {
      label: {
        type: "doc",
        content,
      },
    },
  });
}

function addZapNodes(
  zsl: ZSL,
  nodes: CanvasData["canvas"]["nodes"],
  assetIdsToNodeIds: Record<string, string>,
): void {
  const nodePositionX = 0;
  let nodePositionY = 0;
  const zaps = zsl.zaps || [];

  for (const zap of zaps) {
    const zapId = zap.id;
    const nodeId = `draft-zap-${zapId}`;
    assetIdsToNodeIds[zapId] = nodeId;
    nodes.push({
      id: nodeId,
      type: "zap",
      position: { x: nodePositionX, y: nodePositionY },
      data: { zapId, label: zap.title },
    });
    nodePositionY += NODE_POSITION_OFFSET_Y;
  }
}

function addTableNodes(
  zsl: ZSL,
  nodes: CanvasData["canvas"]["nodes"],
  assetIdsToNodeIds: Record<string, string>,
): void {
  const nodePositionX = 800;
  let nodePositionY = 0;
  const tables = zsl.tables || [];

  for (const table of tables) {
    const tableId = table.id;
    const nodeId = `draft-table-${tableId}`;
    assetIdsToNodeIds[tableId] = nodeId;
    nodes.push({
      id: nodeId,
      type: "default",
      position: { x: nodePositionX, y: nodePositionY },
      data: {
        tableId,
        label: table.name,
        app: "Tables by Zapier",
        services: [{ currentImplementation: { id: "TableCLIAPI@1.15.12" } }],
      },
    });
    nodePositionY += NODE_POSITION_OFFSET_Y;
  }
}

function addChatbotNodes(
  zsl: ZSL,
  nodes: CanvasData["canvas"]["nodes"],
  assetIdsToNodeIds: Record<string, string>,
): void {
  const nodePositionX = -400;
  let nodePositionY = 0;
  const chatbots = zsl.chatbots || [];

  for (const chatbot of chatbots) {
    const chatbotId = chatbot.id;
    const nodeId = `draft-chatbot-${chatbotId}`;
    assetIdsToNodeIds[chatbotId] = nodeId;
    nodes.push({
      id: nodeId,
      type: "default",
      position: { x: nodePositionX, y: nodePositionY },
      data: {
        chatbotId,
        label: chatbot.config?.chatbotName,
        app: "Zapier Chatbots",
        services: [{ currentImplementation: { id: "ChatbotsCLIAPI@0.0.10" } }],
      },
    });
    nodePositionY += NODE_POSITION_OFFSET_Y;
  }
}

function addInterfaceNodes(
  zsl: ZSL,
  nodes: CanvasData["canvas"]["nodes"],
  assetIdsToNodeIds: Record<string, string>,
): void {
  const nodePositionX = 400;
  let nodePositionY = 0;
  const interfaces = zsl.interfaces || [];

  for (const interface_ of interfaces) {
    const interfaceId = interface_.id;
    const pages = interface_.pages || [];

    for (const page of pages) {
      let nodeTitle = page.title;
      if (!nodeTitle) {
        const blocks = page.blocks || [];
        for (const block of blocks) {
          const blockType = block.type || "";
          nodeTitle = BLOCK_TYPE_TO_NODE_TITLE[blockType] || "";
          if (nodeTitle) break;
        }
      }

      const pageId = page.id;
      const nodeId = `draft-interface-${interfaceId}.${pageId}`;
      assetIdsToNodeIds[`${interfaceId}.${pageId}`] = nodeId;
      nodes.push({
        id: nodeId,
        type: "default",
        position: { x: nodePositionX, y: nodePositionY },
        data: {
          projectId: interfaceId,
          pageId,
          label: nodeTitle,
          app: "Interfaces by Zapier",
          services: [
            { currentImplementation: { id: "InterfacesCLIAPI@1.3.4" } },
          ],
        },
      });
      nodePositionY += NODE_POSITION_OFFSET_Y;
    }
  }
}

function addZapEdges(
  zsl: ZSL,
  edges: CanvasData["canvas"]["edges"],
  assetIdsToNodeIds: Record<string, string>,
  warnings: string[],
): void {
  const zaps = zsl.zaps || [];
  for (const zap of zaps) {
    const zapId = zap.id;
    const steps = zap.zdl?.steps || [];
    for (const step of steps) {
      const tableId = step.params?.table_id;
      if (tableId) {
        addEdge(edges, assetIdsToNodeIds, tableId, zapId, warnings);
      }
      const pageId = step.params?.pageId;
      if (pageId) {
        addEdge(edges, assetIdsToNodeIds, pageId, zapId, warnings);
      }
      const stepsInner1 = step.steps || [];
      for (const stepInner1 of stepsInner1) {
        const stepsInner2 = stepInner1.steps || [];
        for (const stepInner2 of stepsInner2) {
          const tableId = stepInner2.params?.table_id;
          if (tableId) {
            addEdge(edges, assetIdsToNodeIds, tableId, zapId, warnings);
          }
        }
      }
    }
  }
}

function addChatbotEdges(
  zsl: ZSL,
  edges: CanvasData["canvas"]["edges"],
  assetIdsToNodeIds: Record<string, string>,
  warnings: string[],
): void {
  const chatbots = zsl.chatbots || [];
  for (const chatbot of chatbots) {
    const chatbotId = chatbot.id;
    const triggers = chatbot.config?.triggers || [];
    for (const trigger of triggers) {
      const tableId = trigger.config?.collectUserDataTableId;
      if (tableId) {
        addEdge(edges, assetIdsToNodeIds, chatbotId, tableId, warnings);
      }
    }
  }
}

function addTableEdges(
  zsl: ZSL,
  edges: CanvasData["canvas"]["edges"],
  assetIdsToNodeIds: Record<string, string>,
  warnings: string[],
): void {
  const tables = zsl.tables || [];
  for (const table of tables) {
    const tableId = table.id;
    const fields = table.fields || [];
    for (const field of fields) {
      const targetTableId = field.options?.choice_provider?.with_table_id;
      if (targetTableId) {
        addEdge(edges, assetIdsToNodeIds, tableId, targetTableId, warnings);
      }
    }
  }
}

function addInterfaceEdges(
  zsl: ZSL,
  edges: CanvasData["canvas"]["edges"],
  assetIdsToNodeIds: Record<string, string>,
  warnings: string[],
): void {
  const interfaces = zsl.interfaces || [];
  for (const interface_ of interfaces) {
    const interfaceId = interface_.id;
    const pages = interface_.pages || [];
    for (const page of pages) {
      const pageId = page.id;
      const sourceAssetId = `${interfaceId}.${pageId}`;
      const blocks = page.blocks || [];
      for (const block of blocks) {
        const config = block.config || {};
        const tableId = config.tableId;
        if (tableId) {
          addEdge(edges, assetIdsToNodeIds, sourceAssetId, tableId, warnings);
        }
        const storageId = config.storageId;
        if (storageId) {
          addEdge(edges, assetIdsToNodeIds, sourceAssetId, storageId, warnings);
        }
        const chatbotId = config.chatbotId;
        if (chatbotId) {
          addEdge(edges, assetIdsToNodeIds, sourceAssetId, chatbotId, warnings);
        }
        const triggers = config.triggers || [];
        for (const trigger of triggers) {
          const targetPageId = trigger.config?.pageId;
          if (targetPageId) {
            const targetAssetId = `${interfaceId}.${targetPageId}`;
            addEdge(
              edges,
              assetIdsToNodeIds,
              sourceAssetId,
              targetAssetId,
              warnings,
            );
          }
        }
        const children = block.children || [];
        for (const child of children) {
          const config = child.config || {};
          const tableId = config.tableId;
          if (tableId) {
            addEdge(edges, assetIdsToNodeIds, sourceAssetId, tableId, warnings);
          }
          const targetPageId = config.pageId;
          if (targetPageId) {
            const targetAssetId = `${interfaceId}.${targetPageId}`;
            addEdge(
              edges,
              assetIdsToNodeIds,
              sourceAssetId,
              targetAssetId,
              warnings,
            );
          }
        }
      }
    }
  }
}

function addEdge(
  edges: CanvasData["canvas"]["edges"],
  assetIdsToNodeIds: Record<string, string>,
  sourceAssetId: string,
  targetAssetId: string,
  warnings: string[],
): void {
  const [sourceId, targetId] = replaceTemplateString(
    sourceAssetId,
    targetAssetId,
  );

  if (!assetIdsToNodeIds[sourceId]) {
    warnings.push(
      `Failed to add edge between ${sourceId} to ${targetId}: ${sourceId} does not exist`,
    );
    return;
  }

  if (!assetIdsToNodeIds[targetId]) {
    warnings.push(
      `Failed to add edge between ${sourceId} to ${targetId}: ${targetId} does not exist`,
    );
    return;
  }

  if (assetIdsToNodeIds[sourceId] === assetIdsToNodeIds[targetId]) {
    return;
  }

  const edge = {
    source: assetIdsToNodeIds[sourceId],
    target: assetIdsToNodeIds[targetId],
    id: `${assetIdsToNodeIds[sourceId]}=>${assetIdsToNodeIds[targetId]}`,
    markerEnd: {
      type: "arrow",
      strokeWidth: 1.5,
      color: "#64748B", // neutral700
    },
  };

  if (!edges.some((e) => e.id === edge.id)) {
    edges.push(edge);
  }
}

function replaceTemplateString(
  sourceAssetId: string,
  targetAssetId: string,
): [string, string] {
  const patterns = [
    /\${{\s*tables\.(\S+)\s*}}/,
    /\${{\s*chatbots\.(\S+)\s*}}/,
    /\${{\s*interfaces\.(\S+)\s*}}/,
  ];

  for (const pattern of patterns) {
    const sourceMatch = sourceAssetId.match(pattern);
    if (sourceMatch) {
      sourceAssetId = sourceMatch[1];
    }
    const targetMatch = targetAssetId.match(pattern);
    if (targetMatch) {
      targetAssetId = targetMatch[1];
    }
  }

  return [sourceAssetId, targetAssetId];
}
